import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Avatar,
  Stack,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PolicyIcon from '@mui/icons-material/Policy';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const items = [
  {
    key: 'privacyPolicy',
    title: 'Privacy Policy',
    desc: 'Edit the public privacy policy',
    editPath: '/dashboard/privacy-policy',
    viewPath: '/privacy-policy',
    icon: <PolicyIcon sx={{ color: '#2263f3' }} />,
    label: 'Public',
  },
  {
    key: 'termsProvider',
    title: 'Service Provider Terms and Conditions',
    desc: 'Edit provider-facing terms',
    editPath: '/dashboard/terms-provider',
    viewPath: '/terms-provider',
    icon: <GavelIcon sx={{ color: '#2263f3' }} />,
    label: 'Provider',
  },
  {
    key: 'termsCustomer',
    title: 'Customer Terms and Conditions',
    desc: 'Edit customer-facing terms',
    editPath: '/dashboard/terms-customer',
    viewPath: '/terms-customer',
    icon: <DescriptionIcon sx={{ color: '#2263f3' }} />,
    label: 'Customer',
  },
];

const PoliciesAdmin = () => {
  const nav = useNavigate();
  const [counts, setCounts] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const c = {};
      await Promise.all(
        items.map(async (it) => {
          try {
            const d = await getDoc(doc(db, 'settings', it.key));
            if (d.exists()) {
              const data = d.data();
              if (Array.isArray(data.sections)) c[it.key] = data.sections.length;
              else if (typeof data.content === 'string') c[it.key] = data.content ? 1 : 0;
              else c[it.key] = 0;
            } else {
              c[it.key] = 0;
            }
          } catch (err) {
            c[it.key] = 0;
          }
        })
      );
      if (mounted) setCounts(c);
    };
    load();
    return () => { mounted = false; };
  }, []);

  const openPreview = async (key) => {
    setPreviewKey(key);
    setLoadingPreview(true);
    setPreviewOpen(true);
    try {
      const d = await getDoc(doc(db, 'settings', key));
      if (d.exists()) setPreviewData(d.data());
      else setPreviewData(null);
    } catch (err) {
      setPreviewData(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewKey(null);
    setPreviewData(null);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Policy Management</Typography>
        <Typography variant="body2" color="text.secondary">Manage all platform policies</Typography>
      </Box> */}

      <Stack spacing={2}>
        {items.map((it) => (
          <Paper key={it.key} elevation={2} sx={{ p: 2.5, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: '#EAF0FF', width: 56, height: 56 }}>
                {it.icon}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{it.title}</Typography>
                <Link component="button" underline="hover" onClick={() => openPreview(it.key)} sx={{ display: 'block', color: '#1E88FF', fontSize: '0.9rem' }}>{it.label}</Link>
                <Typography variant="caption" color="text.secondary">{(counts[it.key] ?? '—') + ' sections'}</Typography>
              </Box>
            </Box>

            <Box>
              <Button variant="outlined" onClick={() => openPreview(it.key)} sx={{ mr: 1, textTransform: 'none', borderRadius: 2 }}>View</Button>
              <Button variant="contained" onClick={() => nav(it.editPath)} sx={{ textTransform: 'none', borderRadius: 2 }}>Edit</Button>
            </Box>
          </Paper>
        ))}
      </Stack>
      
      <Dialog fullWidth maxWidth="md" open={previewOpen} onClose={closePreview}>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{previewKey === 'privacyPolicy' ? 'Privacy Policy' : previewKey === 'termsProvider' ? 'Provider Terms' : previewKey === 'termsCustomer' ? 'Customer Terms' : ''}</Typography>
          <IconButton onClick={closePreview}><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent dividers sx={{ minHeight: 240 }}>
          {loadingPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : previewData ? (
            <Box>
              {Array.isArray(previewData.sections) ? (
                previewData.sections.map((sec, i) => (
                  <Box key={sec.id || i} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{`${i + 1}. ${sec.heading || ''}`}</Typography>
                    {Array.isArray(sec.blocks) ? sec.blocks.map((b, j) => (
                      <Typography key={b.id || j} variant="body2" sx={{ mb: 1 }}>{b.content}</Typography>
                    )) : null}
                  </Box>
                ))
              ) : (
                <Typography variant="body2">{previewData.content || 'No content available.'}</Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2">No content available.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default PoliciesAdmin;
